import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteNegativeDocumentCancellationMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['branch'] as const;

export function InvoiceCanceling() {
  const navigate = useNavigate();
  const [deleteDefinition] = useDeleteNegativeDocumentCancellationMutation();

  const handleActionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = useCallback(
    ({actionKey, rowId, getColumnData, refreshData}) => {
      const id = isArray(rowId) ? head(rowId) : rowId;

      const branchName = getColumnData<{label: string}>('branch')?.value?.label;

      if (isNil(id)) {
        showNotification.error();
        return;
      }

      const handleDeleteDefinition = (id: string) =>
        deleteDefinition({id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);

      match(actionKey)
        .with('DETAIL', 'EDIT', () =>
          navigate(composePath(settingsRoutes.invoiceCancelingDetail, {params: {id}}))
        )
        .with('delete', () =>
          openDeleteDialog({
            onConfirm: () => handleDeleteDefinition(id),
            text: i18n.t('entity.accounting.labels.deleteDefinitionDeleteText', {
              name: branchName,
            }),
          })
        )
        .with('edit', () =>
          navigate(composePath(settingsRoutes.invoiceCancelingDetail, {params: {id}}))
        )
        .otherwise(() => showNotification.warning('unknown action key'));
    },
    [deleteDefinition, navigate]
  );

  const title = i18n.t('page.accounting.labels.invoiceCancel');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        data-testid={testIds.settings.invoiceCanceling('header')}
        header={{
          title,
          actions: [
            {
              type: 'button',
              title: i18n.t('page.accounting.labels.newDefinition'),
              onClick: () => navigate(settingsRoutes.newInvoiceCanceling),
            },
          ],
        }}
        description={i18n.t('page.accounting.labels.invoiceCancelDescription')}
      >
        <DmsDataGrid
          gridCode="negative-document-cancellation-definition"
          actionCallback={handleActionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.invoiceCanceling('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
